import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  FlatList, Text, StyleSheet, View,
} from 'react-native';
import Moment from 'moment';
import { useSelector } from 'react-redux';
import PlusIcon from '../../../../../../components/16pxIcons/PlusIcon';
import PressableStyled from '../../../../../../components/PressableStyled';
import { AssetInstance, AssetNote, UserPermission } from '../../../../../../constants/Types';
import Colors from '../../../../../../constants/Colors';
import UpdateNoteModal from '../UpdateNoteModal';
import NoteItem from './components/NoteItem';
import isMobile from '../../../../../../components/IsMobile';

interface NotesProps {
  asset: AssetInstance,
  setSelectedAsset: Dispatch<SetStateAction<AssetInstance>>,
  showScreenAlert,
}

const NotesSection = ({ asset, setSelectedAsset, showScreenAlert }: NotesProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const [addModalShown, setAddModalShown] = useState<boolean>(false);
  const [editModalShown, setEditModalShown] = useState<AssetNote | null>(null);
  const [noteViewMode, setNoteViewMode] = useState<boolean>(false);
  const canEditAsset: boolean = isAdmin
    || permissions.includes(UserPermission.ASSETS_UPDATE);

  return <View
    testID='notes-section'
    style={styles.container}
  >
    {isMobile ? <PressableStyled
      style={styles.addNoteButton}
      onPress={() => setAddModalShown(true)}
    >
      <Text style={styles.addNoteText}>{'Add note'}</Text>
      <PlusIcon color={styles.addNoteText.color} />
    </PressableStyled> : <Text style={styles.title}>{'Notes'}</Text>}
    <FlatList
      style={styles.notesList}
      data={asset.notes?.sort((prev: AssetNote, next: AssetNote) => new Date(next.updatedOn
        ?? next.createdOn).getTime() - new Date(prev.updatedOn ?? prev.createdOn).getTime())}
      renderItem={({ item: note, index }) => <NoteItem
        {...{
          note, editModalShown, setEditModalShown, setNoteViewMode,
        }}
        testID={`asset-note#${index + 1}`}
      />}
      ListEmptyComponent={<Text style={styles.description}>
        {'No notes have been created yet for this asset'}
      </Text>}
      keyExtractor={(note: AssetNote) => note.id}
    />
    {!isMobile && canEditAsset && <PressableStyled
      style={styles.addNoteButton}
      onPress={() => setAddModalShown(true)}
    >
      <Text style={styles.addNoteText}>{'Add note'}</Text>
      <PlusIcon color={styles.addNoteText.color} />
    </PressableStyled>}
    <UpdateNoteModal
      isVisible={addModalShown}
      hide={() => setAddModalShown(false)}
      {...{ showScreenAlert }}
      addNote={(note: AssetNote) => {
        asset.notes.push(note);
      }}
      assetId={asset.id}
    />
    <UpdateNoteModal
      isVisible={!!editModalShown}
      hide={() => setEditModalShown(null)}
      {...{ showScreenAlert, noteViewMode, setNoteViewMode }}
      note={editModalShown}
      editNote={(newNote: AssetNote) => {
        const updatedNotes: AssetNote[] = asset.notes.map((n: AssetNote) => ({
          ...n,
          ...(n.id === newNote.id
            ? { ...newNote, updatedOn: Moment().toISOString() } : {}),
        }));
        setSelectedAsset({
          ...asset,
          notes: updatedNotes,
        });
      }}
      deleteNote={(noteId: string) => {
        setSelectedAsset({
          ...asset,
          notes: asset.notes.filter((n: AssetNote) => n.id !== noteId),
        });
      }}
      assetId={asset.id}
    />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginBottom: 8,
  },
  title: {
    fontSize: 20,
    lineHeight: 32,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  description: {
    fontSize: 12,
    lineHeight: 14,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    marginHorizontal: 12,
  },
  addNoteButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  addNoteText: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlue500,
    marginEnd: 4,
  },
  notesList: {
    marginTop: 24,
    marginHorizontal: isMobile ? -24 : -12,
    paddingHorizontal: isMobile ? 24 : undefined,
  },
});

export default NotesSection;
